import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { ImagePlus, X, Camera } from 'lucide-react';
import api from '../lib/api';

const AuthImage = ({ src, alt, className }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!src) return;
    api.get(src, { responseType: 'blob' })
      .then((res) => {
        if (!cancelled) setBlobUrl(URL.createObjectURL(res.data));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src]);

  if (error || !blobUrl) {
    return <div className={`${className} bg-muted flex items-center justify-center`}><Camera className="w-6 h-6 text-muted-foreground" /></div>;
  }
  return <img src={blobUrl} alt={alt} className={className} />;
};

const ImageGallery = ({ images = [], onUpload, onDelete, editable = false, maxImages = 10, label = "Images" }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        await onUpload({ filename: file.name, file_data: base64 });
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (images.length === 0 && !editable) return null;

  return (
    <div data-testid="image-gallery">
      {editable && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          {images.length < maxImages && (
            <>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} data-testid="image-file-input" />
              <Button
                variant="outline"
                size="sm"
                className="border-gold/30 text-gold hover:bg-gold/10 h-7 text-xs"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-image-btn"
              >
                <ImagePlus className="w-3 h-3 mr-1" />
                {uploading ? 'Uploading...' : 'Add Photo'}
              </Button>
            </>
          )}
        </div>
      )}

      {images.length === 0 && editable && (
        <button
          onClick={() => fileInputRef.current?.click()}
          className="w-full py-8 border border-dashed border-[#d1d5db] hover:border-gold/30 transition-colors flex flex-col items-center gap-2"
          data-testid="upload-placeholder"
        >
          <Camera className="w-8 h-8 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Click to upload photos</span>
        </button>
      )}

      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {images.map((img) => (
            <div key={img.id} className="relative group aspect-[4/3] bg-background overflow-hidden" data-testid={`image-${img.id}`}>
              <AuthImage
                src={img.url}
                alt={img.filename}
                className="w-full h-full object-cover"
              />
              {editable && onDelete && (
                <button
                  onClick={() => onDelete(img.id)}
                  className="absolute top-1 right-1 w-6 h-6 bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-image-${img.id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
